import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/cn";

export type AlertTone = "error" | "warning" | "info" | "success";

/**
 * A soft filled callout, not a bordered notice. The fill carries the tone and
 * the icon repeats it, so the message is readable before it is read.
 */
const TONE: Record<AlertTone, { box: string; icon: string; glyph: LucideIcon }> = {
  // Neutral by design: most "info" is context, not an alarm.
  info: { box: "bg-ink-100 text-fg", icon: "text-fg-muted", glyph: Info },
  warning: { box: "bg-ochre-50 text-ochre-700", icon: "text-ochre-600", glyph: TriangleAlert },
  error: { box: "bg-alert-50 text-alert-700", icon: "text-alert-600", glyph: CircleAlert },
  success: { box: "bg-go-50 text-go-700", icon: "text-go-600", glyph: CircleCheck },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * A message about the state of the page.
 */
export function Alert({ tone = "error", title, children, className }: AlertProps) {
  const { box, icon, glyph: Glyph } = TONE[tone];

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-md px-4 py-3.5 text-base", box, className)}
    >
      <Glyph className={cn("mt-px size-4 shrink-0", icon)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title ? <p className="text-base font-semibold">{title}</p> : null}
        {children ? <div className={cn("text-base", title && "mt-1")}>{children}</div> : null}
      </div>
    </div>
  );
}
